/*============================================================================*/

/**
 * Helper functions
 *
 * Provides general-purpose utility functions that can be reused
 * across the project. These are not tied to any specific feature,
 * component, or business logic, but serve as building blocks to
 * simplify repetitive tasks.
 */

/*============================================================================*/


/**
 * Capitalizes the first letter of a given string.
 *
 * @param {Object} params
 * @param {string} params.text - Input string to be transformed.
 * @returns {string} - String with the first letter uppercased.
 */
function ucfirst({ text = '' } = {})
{
    if (typeof text !== 'string' || text.length === 0) return text;
    return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Ensure that a given URL uses HTTPS instead of HTTP.
 *
 * @param {Object} params - Function parameters.
 * @param {string} params.url - The input URL to transform.
 * @returns {string} - The secure HTTPS URL.
 */
function forceHttps({ url })
{
    if (typeof url !== 'string')
    {
        throw new Error('Invalid URL: expected a string.');
    }

    if (url.startsWith('http://'))
    {
        return url.replace(/^http:\/\//i, 'https://');
    }

    return url;
}

/**
 * Check if a value is empty
 *
 * Works similarly to PHP's empty(). Considers empty:
 * - Arrays with length 0
 * - Objects with no keys
 * - Strings that are empty, whitespace-only, or "unset"
 * - Booleans that are false
 * - null or undefined
 *
 * @param {*} value - The value to check
 * @returns {boolean} True if the value is considered empty
 */
function isEmpty(value)
{
    if (Array.isArray(value))
    {
        return value.length === 0;
    }

    if (value && typeof value === 'object')
    {
        return Object.keys(value).length === 0;
    }

    if (typeof value === 'string')
    {
        return value.trim().length === 0 || value === 'unset';
    }

    if (typeof value === 'boolean')
    {
        return value === false;
    }

    return value === null || value === undefined;
}

/**
 * Preloads images to reduce flicker or transition issues.
 *
 * Creates Image objects for each URL, ensuring each image is only
 * loaded once per session to optimize performance and transitions.
 *
 * @param {Object} args - Function arguments
 * @param {string[]} args.urls - Array of image URLs to preload
 */
let preloadedImages = new Set();
function preloadImages({ urls = [] })
{
    urls.forEach((url) =>
    {
        if (!isEmpty(url) && !preloadedImages.has(url))
        {
            const img = new Image();
            img.src = url;
            preloadedImages.add(url);
        }
    });
}

/**
 * Debounce utility function
 *
 * Delays the execution of the given function until after
 * a certain time (`wait`) has passed since the last call.
 * Useful for optimizing events like window resize or keyup.
 *
 * @param {Object} params
 * @param {Function} params.func - Function to debounce
 * @param {number} [params.wait=300] - Delay in milliseconds
 * @returns {Function} Debounced function
 */
function debounce({ func, wait = 300 } = {})
{
    let timeout

    return function(...args)
    {
        const context = this

        const later = function()
        {
            clearTimeout(timeout)
            func.apply(context, args)
        }

        clearTimeout(timeout)
        timeout = setTimeout(later, wait)
    }
}

/**
 * Extracts variant nodes from a Storefront API product node.
 *
 * @param {Object} productNode - Product node from GraphQL.
 * @returns {Object[]} Variant nodes.
 */
function getProductVariants(productNode)
{
    return (productNode.variants?.edges || [])
        .map(edge => edge.node)
        .filter(Boolean);
}

/**
 * Resolves product sellability from Storefront API product/variant data.
 *
 * @param {Object} productNode - Product node from GraphQL.
 * @returns {{ isAvailable: boolean, cartVariant: Object|null }}
 */
function resolveProductSellability(productNode)
{
    const variants = getProductVariants(productNode);
    const cartVariant = variants.find(v => v.availableForSale === true) || variants[0] || null;
    const isAvailable = productNode.availableForSale === true && cartVariant?.availableForSale === true;

    return { isAvailable, cartVariant };
}

/**
 * Parses manual discount metaobject field values from Storefront API.
 *
 * @param {string|number|null} rawPercentageValue - Raw percentage field value.
 * @param {string|number|null} rawFlatValue - Raw money field value (JSON string or amount).
 * @returns {{ percentValue: number, flatValue: number }}
 */
function parseManualDiscountFields(rawPercentageValue, rawFlatValue)
{
    const percentValue = (rawPercentageValue === undefined || rawPercentageValue === null || rawPercentageValue === '')
        ? 0
        : (parseFloat(rawPercentageValue) || 0);

    let flatValue = 0;

    if (rawFlatValue !== undefined && rawFlatValue !== null && rawFlatValue !== '')
    {
        if (typeof rawFlatValue === 'number')
        {
            flatValue = rawFlatValue;
        }
        else
        {
            const rawFlatString = String(rawFlatValue).trim();

            if (rawFlatString.charAt(0) === '{')
            {
                try
                {
                    const parsedMoney = JSON.parse(rawFlatString);
                    flatValue = parseFloat(parsedMoney?.amount) || 0;
                }
                catch (error)
                {
                    flatValue = 0;
                }
            }
            else
            {
                flatValue = parseFloat(rawFlatString) || 0;
            }
        }
    }

    return { percentValue, flatValue };
}

/**
 * Resolves display prices using compare-at, then manual percentage, then manual flat discount.
 *
 * @param {Object} params
 * @param {number|string} [params.price=0] - Base price amount.
 * @param {number|string|null} [params.compareAtPrice=null] - Compare-at price amount.
 * @param {Object} [params.manualDiscount={}] - Manual discount fields (Shopify or WP shapes).
 * @param {boolean} [params.priceAsCents=false] - Whether amounts are in cents.
 * @returns {{ finalPrice: number, originalPrice: number, hasDiscount: boolean }}
 */
function resolveProductPrices({ price = 0, compareAtPrice = null, manualDiscount = {}, priceAsCents = false } = {})
{
    const basePrice = parseFloat(price) || 0;
    const compareParsed = (compareAtPrice !== null && compareAtPrice !== '' && compareAtPrice !== undefined) ? (parseFloat(compareAtPrice) || 0) : null;

    const percentage = parseFloat(manualDiscount?.percentage || manualDiscount?.manualDiscountByPercentageValue || manualDiscount?.rawManualDiscountByPercentageValue) || 0;
    let flat = parseFloat(manualDiscount?.flat || manualDiscount?.manualDiscountByFlatValue) || 0;

    let finalPrice = basePrice;
    let originalPrice = basePrice;
    let hasDiscount = false;

    if (compareParsed !== null && compareParsed > basePrice)
    {
        originalPrice = compareParsed;
        finalPrice = basePrice;
        hasDiscount = true;
    }
    else if (percentage > 0)
    {
        originalPrice = basePrice;
        finalPrice = basePrice * (1 - (percentage / 100));
        hasDiscount = finalPrice < originalPrice;
    }
    else if (flat > 0)
    {
        originalPrice = basePrice;
        const flatDiscountAmount = priceAsCents ? (flat * 100) : flat;
        finalPrice = Math.max(0, basePrice - flatDiscountAmount);
        hasDiscount = finalPrice < originalPrice;
    }

    if (priceAsCents)
    {
        finalPrice = Math.round(finalPrice);
        originalPrice = Math.round(originalPrice);
    }

    if (!hasDiscount)
    {
        originalPrice = 0;
    }

    return { finalPrice, originalPrice, hasDiscount };
}

/**
 * Resolves whether a product card should render as available for purchase.
 *
 * @param {Object} product - Product data passed to Comp_ProductCard.
 * @param {Object} args - Comp_ProductCard arguments.
 * @returns {boolean}
 */
function resolveProductCardAvailability(product, args)
{
    const safeArgs = args || {};
    const safeProduct = product || {};

    if (typeof safeArgs.available === 'boolean')
    {
        return safeArgs.available;
    }

    if (safeProduct.availableForSale === true && safeProduct.cartVariantAvailableForSale !== false)
    {
        return true;
    }

    if (safeProduct.availableForSale === false || safeProduct.cartVariantAvailableForSale === false)
    {
        return false;
    }

    return safeProduct.available === true;
}